import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { Subscription } from 'rxjs';

import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { AttributeDataService } from '../../services/attribute-data.service';
import { ComponentsService } from '../../services/components.service';
import { TemplateEditorService } from '../../services/template-editor.service';
import { TemplatesService } from 'src/app/presentations/services/templates.service';

export type OrientationType =
  | 'landscape'
  | 'portrait'
  | 'responsive'
  | 'custom';

interface OrientationOption {
  type: OrientationType;
  label: string;
  description: string;
  icon: string;
  ratio?: string;
}

@Component({
  selector: 'presentation-orientation-selector',
  templateUrl: './presentation-orientation-selector.component.html',
  styleUrls: ['./presentation-orientation-selector.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
  ],
})
export class PresentationOrientationSelectorComponent
  implements OnInit, OnDestroy
{
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
  isVisible = false;
  visibleOptions: OrientationOption[] = [];
  isOpen = false;
  selectedOrientation: OrientationType = 'responsive';
  showCustomFields = false;
  customWidth = 1920;
  customHeight = 1080;

  readonly options: OrientationOption[] = [
    {
      type: 'landscape',
      label: 'Landscape',
      description: 'For only landscape screens',
      icon: 'display',
      ratio: '16:9',
    },
    {
      type: 'portrait',
      label: 'Portrait',
      description: 'For only portrait screens',
      icon: 'portrait',
      ratio: '9:16',
    },
    {
      type: 'responsive',
      label: 'Responsive',
      description: 'Auto adapts to portrait & landscape',
      icon: 'responsive',
    },
    {
      type: 'custom',
      label: 'Custom',
      description: 'Enter your own dimensions',
      icon: 'add',
    },
  ];

  private _historySubscription: Subscription | null = null;

  private readonly LANDSCAPE_WIDTH = 1920;
  private readonly LANDSCAPE_HEIGHT = 1080;
  private readonly PORTRAIT_WIDTH = 1080;
  private readonly PORTRAIT_HEIGHT = 1920;

  constructor(
    private attributeDataService: AttributeDataService,
    private componentsService: ComponentsService,
    private broadcaster: BroadcasterService,
    private templateEditorService: TemplateEditorService,
    private templatesService: TemplatesService,
  ) {}

  ngOnInit(): void {
    void this.initVisibility();

    this._historySubscription = this.broadcaster.on(
      'risevision.template-editor.historyRestored',
      () => {
        if (this.isVisible) {
          this.load();
        }
      },
    );
  }

  ngOnDestroy(): void {
    this._historySubscription?.unsubscribe();
  }

  get currentOption(): OrientationOption {
    return (
      this.visibleOptions.find((o) => o.type === this.selectedOrientation) ||
      this.visibleOptions[0] ||
      this.options[0]
    );
  }

  onMenuOpened(): void {
    this.isOpen = true;
    this.load();
  }

  onMenuClosed(): void {
    this.isOpen = false;
    this.showCustomFields = false;
  }

  selectOrientation(type: OrientationType): void {
    this.selectedOrientation = type;

    if (type === 'custom') {
      this.showCustomFields = true;
      return;
    }

    this.showCustomFields = false;

    const { width, height } = this.getDimensionsForType(type);
    this.save(width, height);
    this.close();
  }

  applyCustom(): void {
    if (this.customWidth > 0 && this.customHeight > 0) {
      this.save(this.customWidth, this.customHeight);
      this.close();
    }
  }

  cancelCustom(): void {
    this.isOpen = false;
    this.load();
  }

  private close(): void {
    this.isOpen = false;
    this.showCustomFields = false;
    this.menuTrigger?.closeMenu();
  }

  private getDimensionsForType(type: OrientationType): {
    width: number;
    height: number;
  } {
    switch (type) {
      case 'portrait':
        return { width: this.PORTRAIT_WIDTH, height: this.PORTRAIT_HEIGHT };
      case 'responsive':
      case 'landscape':
      default:
        return { width: this.LANDSCAPE_WIDTH, height: this.LANDSCAPE_HEIGHT };
    }
  }

  private save(width: number, height: number): void {
    this.attributeDataService.moveComponentsIntoView(width, height);
    this.componentsService.highlightComponent(null);

    const orientationPreset =
      this.selectedOrientation === 'responsive'
        ? null
        : this.selectedOrientation;

    this.attributeDataService.setAttributeDataGlobal(
      'orientationPreset',
      orientationPreset,
    );
    this.attributeDataService.setAttributeDataGlobal('widthPreset', width);
    this.attributeDataService.setAttributeDataGlobal('heightPreset', height);

    this.broadcaster.emit(
      'risevision.template-editor.presentationOrientationChanged',
    );
  }

  public isCustomOptionVisible(): boolean {
    return this.templatesService.isBlankTemplate(
      this.templateEditorService.presentation?.productCode,
    );
  }

  private isResponsiveOptionVisible(): boolean {
    //The method is a placeholder for future template-based filtering
    return true;
  }

  private populateVisibleOptions(): void {
    const options: OrientationOption[] = [
      this.getOption('landscape'),
      this.getOption('portrait'),
    ];

    if (this.isResponsiveOptionVisible()) {
      options.push(this.getOption('responsive'));
    }

    if (this.isCustomOptionVisible()) {
      options.push(this.getOption('custom'));
    }

    this.visibleOptions = options;
  }

  private getOption(type: OrientationType): OrientationOption | undefined {
    return this.options.find((option) => option.type === type);
  }

  private async initVisibility(): Promise<void> {
    const productCode = this.templateEditorService.presentation?.productCode;
    if (!productCode) {
      this.isVisible = false;
      return;
    }

    try {
      const { items } = await this.templatesService.getCachedTemplatesByIds([
        productCode,
      ]);
      const template = items?.[0];
      this.isVisible = template ? this.isTemplateResponsive(template) : false;
    } catch (error) {
      this.isVisible = false;
    }

    if (!this.isVisible) {
      return;
    }

    this.populateVisibleOptions();
    this.load();
  }

  private isTemplateResponsive(template: {
    templateCategories?: unknown;
  }): boolean {
    const categories = template?.templateCategories;
    return Array.isArray(categories) && categories.includes('Responsive');
  }

  private load(): void {
    const width =
      parseInt(
        this.attributeDataService.getAttributeDataGlobal('widthPreset'),
        10,
      ) || this.LANDSCAPE_WIDTH;
    const height =
      parseInt(
        this.attributeDataService.getAttributeDataGlobal('heightPreset'),
        10,
      ) || this.LANDSCAPE_HEIGHT;
    const orientationPreset =
      this.attributeDataService.getAttributeDataGlobal('orientationPreset');

    const defaultOrientation = this.isResponsiveOptionVisible()
      ? 'responsive'
      : 'landscape';

    if (orientationPreset) {
      const hasVisiblePreset = this.visibleOptions.some(
        (option) => option.type === orientationPreset,
      );
      this.selectedOrientation = hasVisiblePreset
        ? orientationPreset
        : defaultOrientation;
      this.showCustomFields =
        orientationPreset === 'custom' && this.isCustomOptionVisible();
    } else {
      this.selectedOrientation = defaultOrientation;
      this.showCustomFields = false;
    }

    this.customWidth = width;
    this.customHeight = height;
  }
}
